/* eslint-disable no-param-reassign */
import {
  createSlice,
  current,
  type PayloadAction,
  type Slice,
} from '@reduxjs/toolkit';

import type {
  AlignmentTypes,
  BrandLogo,
  DataProvider,
  DuplicateCheckCompositeRule,
  DuplicateCheckConfig,
  DuplicateCheckSingleRule,
  FieldElement,
  FormConfig,
} from '../../../../shared/types';
import type {
  PaletteElement,
  PromptData,
  PropertiesDrawerData,
} from '../../data-layer/types';
import type {
  BuilderUiState,
  SetValidationErrorPayload,
  ShowBuilderAlertPayload,
  UpdateFieldColSpanPayload,
} from '../types/builderTypes';

import {
  ALIGNMENT_PROPERTY_ORIENTATION,
  ORCHESTRATION_CATEGORY,
} from '../../../../shared/constants';
import {
  cleanupOrphanedDataProviders,
  initialPropmtData,
  refreshRowCapacities,
  updateFieldColSpan,
} from '../../data-layer';

const initialState: BuilderUiState = {
  // Alert banner
  alert: {
    isVisible: false,
    bannerType: null,
    message: null,
  },

  // Canvas + design
  canvasFields: [],
  dataProviders: {},
  propertiesDrawerData: {
    isPropertiesOpen: false,
    selectedFieldData: null,
  },
  activeDragItem: null,

  // Design section
  logo: '',
  logoPath: '',
  brandLogo: {
    name: '',
    type: null,
    align: ALIGNMENT_PROPERTY_ORIENTATION.CENTER,
  },
  promptData: initialPropmtData,

  // Modals
  isPreviewModalOpen: false,
  isHttpProviderModalOpen: false,
  isSavOqProviderModalOpen: false,
  isPublishSuccessModalOpen: false,
  isRichTextEditModalOpen: false,

  // Metadata panel
  formName: '',
  formDescription: '',
  formSlug: '',
  customFormUid: '',
  selectedFormCategory: 'USERS',
  selectedUserAction: null,
  selectedUserActionType: 'REST',

  // Orchestration state
  isOrchestrationToggleOn: false,
  selectedOrchestrationProcess: null,
  selectedInvokeMethod: null,
  selectedInvokeMethodActionEnumChild: null,
  selectedOrchestrationCategory: ORCHESTRATION_CATEGORY.SIGNAL,
  orchestrationData: null,

  // Authentication form state
  auth: true,

  // Field-level validation errors (builder UI validation, not form metadata)
  validationErrors: {},

  // Tools accordion expansion
  expandedToolAccordionTitle: null,

  // Duplicate check / unique key config
  duplicateCheckConfig: null,
  isUniqueKeyModalOpen: false,
  isCreateUniqueKeyModalOpen: false,
  editingUniqueKeyIndex: null,

  // Form config (submit button label, success message)
  formConfig: {},

  // Logo upload in-flight state
  isLogoUploading: false,

  // Form data loading state
  isFormLoading: false,
};

// Explicit type for declaration emit (TS7056: inferred type exceeds max serialization length)
const builderUiSlice: Slice<BuilderUiState> = createSlice({
  name: 'builder/ui',
  initialState,
  reducers: {
    /* ===== Alerts ===== */
    showBuilderAlert(state, action: PayloadAction<ShowBuilderAlertPayload>) {
      state.alert.isVisible = true;
      state.alert.bannerType = action.payload.bannerType;
      state.alert.message = action.payload.message ?? null;
    },
    hideBuilderAlert(state) {
      state.alert.isVisible = false;
      state.alert.bannerType = null;
      state.alert.message = null;
    },

    /* ===== Canvas + design ===== */
    setCanvasFields(state, action: PayloadAction<FieldElement[]>) {
      state.canvasFields = action.payload;
    },
    setDataProviders(
      state,
      action: PayloadAction<Record<string, DataProvider>>
    ) {
      state.dataProviders = action.payload;
    },
    setPropertiesDrawerData(
      state,
      action: PayloadAction<PropertiesDrawerData>
    ) {
      state.propertiesDrawerData = action.payload;
    },
    setActiveDragItem(
      state,
      action: PayloadAction<FieldElement | PaletteElement | null>
    ) {
      state.activeDragItem = action.payload;
    },

    /* ===== Design section ===== */
    setLogo(state, action: PayloadAction<string>) {
      state.logo = action.payload;
    },
    setLogoPath(state, action: PayloadAction<string>) {
      state.logoPath = action.payload;
    },
    setIsLogoUploading(state, action: PayloadAction<boolean>) {
      state.isLogoUploading = action.payload;
    },
    setIsFormLoading(state, action: PayloadAction<boolean>) {
      state.isFormLoading = action.payload;
    },
    setBrandLogo(state, action: PayloadAction<BrandLogo>) {
      state.brandLogo.name = action.payload.name;
      state.brandLogo.type = action.payload.type;
      state.brandLogo.align = action.payload.align;
    },
    setBrandLogoAlign(state, action: PayloadAction<AlignmentTypes>) {
      state.brandLogo.align = action.payload;
    },
    setPromptData(state, action: PayloadAction<PromptData>) {
      state.promptData = action.payload;
    },

    /* ===== Modals ===== */
    openPreviewModal(state) {
      state.isPreviewModalOpen = true;
      // Clear Builder alerts on overlay open
      state.alert.isVisible = false;
      state.alert.bannerType = null;
      state.alert.message = null;
    },
    closePreviewModal(state) {
      state.isPreviewModalOpen = false;
    },
    openHttpProviderModal(state) {
      state.isHttpProviderModalOpen = true;
    },
    closeHttpProviderModal(state) {
      state.isHttpProviderModalOpen = false;
    },
    openSavOqProviderModal(state) {
      state.isSavOqProviderModalOpen = true;
    },
    closeSavOqProviderModal(state) {
      state.isSavOqProviderModalOpen = false;
    },
    openPublishSuccessModal(state) {
      state.isPublishSuccessModalOpen = true;
    },
    closePublishSuccessModal(state) {
      state.isPublishSuccessModalOpen = false;
    },
    setIsRichTextEditModalOpen(state, action: PayloadAction<boolean>) {
      state.isRichTextEditModalOpen = action.payload;
    },
    /* ===== Metadata panel ===== */
    setFormName(state, action: PayloadAction<string>) {
      state.formName = action.payload;
    },
    setFormDescription(state, action: PayloadAction<string>) {
      state.formDescription = action.payload;
    },
    setFormSlug(state, action: PayloadAction<string>) {
      state.formSlug = action.payload;
    },
    setCustomFormUid(state, action: PayloadAction<string>) {
      state.customFormUid = action.payload;
    },
    setSelectedFormCategory(state, action: PayloadAction<string | null>) {
      state.selectedFormCategory = action.payload;
    },
    setSelectedUserAction(state, action: PayloadAction<string | null>) {
      state.selectedUserAction = action.payload;
    },
    setSelectedUserActionType(state, action: PayloadAction<string | null>) {
      state.selectedUserActionType = action.payload;
    },

    /* ===== Orchestration actions ===== */
    setIsOrchestrationToggleOn(state, action: PayloadAction<boolean>) {
      state.isOrchestrationToggleOn = action.payload;
    },
    setSelectedOrchestrationProcess(
      state,
      action: PayloadAction<string | null>
    ) {
      state.selectedOrchestrationProcess = action.payload;
    },
    setSelectedInvokeMethod(state, action: PayloadAction<string | null>) {
      state.selectedInvokeMethod = action.payload;
    },
    setSelectedInvokeMethodActionEnumChild(
      state,
      action: PayloadAction<string | null>
    ) {
      state.selectedInvokeMethodActionEnumChild = action.payload;
    },
    setSelectedOrchestrationCategory(
      state,
      action: PayloadAction<string | null>
    ) {
      state.selectedOrchestrationCategory = action.payload;
    },
    setOrchestrationData(state, action: PayloadAction<unknown>) {
      state.orchestrationData = action.payload;
    },

    /* ===== Authentication form actions ===== */
    setAuth(state, action: PayloadAction<boolean>) {
      state.auth = action.payload;
    },

    /* ===== Form config actions ===== */
    setFormConfig(state, action: PayloadAction<FormConfig>) {
      state.formConfig = action.payload;
    },
    setSubmitBtnLabel(state, action: PayloadAction<string>) {
      state.formConfig = {
        ...state.formConfig,
        submitBtnLabel: action.payload,
      };
    },
    setSubmitSuccessMsg(state, action: PayloadAction<string>) {
      state.formConfig = {
        ...state.formConfig,
        submitSuccessMsg: action.payload,
      };
    },

    /* ===== Validation error map (builder UI) ===== */
    setValidationError(
      state,
      action: PayloadAction<SetValidationErrorPayload>
    ) {
      const { field, error } = action.payload;

      if (error === null) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { [field]: _, ...rest } = state.validationErrors;

        state.validationErrors = rest;

        return;
      }

      state.validationErrors = { ...state.validationErrors, [field]: error };
    },

    /* ===== Tools accordion ===== */
    setExpandedToolAccordionTitle(state, action: PayloadAction<string | null>) {
      state.expandedToolAccordionTitle = action.payload;
    },

    /* ===== Field operations ===== */
    updateFieldColSpanBoth(
      state,
      action: PayloadAction<UpdateFieldColSpanPayload>
    ) {
      const { rowId, fieldId, colSpan } = action.payload;

      const canvasPlain = current(state.canvasFields) as FieldElement[];

      const next = updateFieldColSpan(canvasPlain, rowId, fieldId, colSpan);
      const refreshed = refreshRowCapacities(next);

      state.canvasFields = refreshed;

      const selected = state.propertiesDrawerData.selectedFieldData;

      if (selected && selected.id === fieldId && selected.type === 'field') {
        state.propertiesDrawerData.selectedFieldData = { ...selected, colSpan };
      }
    },

    /* ===== Duplicate Check / Unique Key ===== */
    setDuplicateCheckConfig(
      state,
      action: PayloadAction<DuplicateCheckConfig | null>
    ) {
      state.duplicateCheckConfig = action.payload;
    },
    openUniqueKeyModal(state) {
      state.isUniqueKeyModalOpen = true;
    },
    closeUniqueKeyModal(state) {
      state.isUniqueKeyModalOpen = false;
    },
    openCreateUniqueKeyModal(state, action: PayloadAction<number | null>) {
      state.isCreateUniqueKeyModalOpen = true;
      state.editingUniqueKeyIndex = action.payload;
    },
    closeCreateUniqueKeyModal(state) {
      state.isCreateUniqueKeyModalOpen = false;
      state.editingUniqueKeyIndex = null;
    },
    addSingleKey(state, action: PayloadAction<DuplicateCheckSingleRule>) {
      if (!state.duplicateCheckConfig) {
        state.duplicateCheckConfig = { single: [], composite: [] };
      }

      state.duplicateCheckConfig.single.push(action.payload);
    },
    removeSingleKey(state, action: PayloadAction<number>) {
      if (state.duplicateCheckConfig) {
        state.duplicateCheckConfig.single.splice(action.payload, 1);
      }
    },
    addCompositeKey(state, action: PayloadAction<DuplicateCheckCompositeRule>) {
      if (!state.duplicateCheckConfig) {
        state.duplicateCheckConfig = { single: [], composite: [] };
      }

      state.duplicateCheckConfig.composite.push(action.payload);
    },
    removeCompositeKey(state, action: PayloadAction<number>) {
      if (state.duplicateCheckConfig) {
        state.duplicateCheckConfig.composite.splice(action.payload, 1);
      }
    },
    updateCompositeKey(
      state,
      action: PayloadAction<{
        index: number;
        rule: DuplicateCheckCompositeRule;
      }>
    ) {
      if (state.duplicateCheckConfig) {
        state.duplicateCheckConfig.composite[action.payload.index] =
          action.payload.rule;
      }
    },
    updateSingleKey(
      state,
      action: PayloadAction<{
        index: number;
        rule: DuplicateCheckSingleRule;
      }>
    ) {
      if (state.duplicateCheckConfig) {
        state.duplicateCheckConfig.single[action.payload.index] =
          action.payload.rule;
      }
    },

    /* ===== Safety net: cleanup orphaned data providers ===== */
    runDataProvidersCleanup(state) {
      let newProviders: Record<string, DataProvider> = state.dataProviders;

      const setDataProvidersShim = (
        next:
          | Record<string, DataProvider>
          | ((
              prev: Record<string, DataProvider>
            ) => Record<string, DataProvider>)
      ) => {
        newProviders = typeof next === 'function' ? next(newProviders) : next;
      };

      cleanupOrphanedDataProviders(
        state.canvasFields,
        state.dataProviders,
        setDataProvidersShim
      );

      state.dataProviders = newProviders;
    },
  },
});

// Type alias avoids TS7056 on declaration emit (complex slice actions type)
export const builderUiReducer = builderUiSlice.reducer;
export const builderUiActions: typeof builderUiSlice.actions =
  builderUiSlice.actions;
